import { Typography } from 'app/components/ui';
import { i18n } from 'app/utils/i18n';
import { useMemo, useState } from 'react';
import { TagAction, TagItemShortcuts } from '../../TagItemShortcuts/TagItemShortcuts';
import { TagWithMetadata } from 'app/api/tag-queries/types';

import './ListItemsTable.scss';

export interface AffectedPage {
  pageId: string;
  pageNumber: number | null;
  sectionSuperId: string;
  sectionName: string;
  count?: number;
}

export interface AffectedTag {
  tagId: string;
  conceptQName: string;
  pageId: string;
  pageNumber: number | null;
  sectionSuperId: string;
  sectionName: string;
  details?: Record<string, string>;
}

export interface ListItemsTableTag {
  id: string;
  label: string;
  type: string;
  locationLabel?: string;
  status: string;
  statusInfo: string;
  affectedCount?: number;
  affectedPages?: AffectedPage[];
  affectedTags?: AffectedTag[];
  code?: string;
}

interface ListItemsTableProps {
  tags: ListItemsTableTag[];
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
  onFindingDetailsClick?: (tag: ListItemsTableTag) => void;
  onNavigateToPage?: (page: AffectedPage) => void;
}

export const ListItemsTable = ({ tags, onFindingDetailsClick, onNavigateToPage }: ListItemsTableProps) => {
  const [sortBy, setSortBy] = useState<'desc' | 'asc'>('asc');

  const handleSortToggle = () => {
    setSortBy((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const sortedSpecifics = useMemo(
    () =>
      [...tags].sort((a, b) => {
        if (sortBy === 'asc') {
          return a.label.localeCompare(b.label);
        } else {
          return b.label.localeCompare(a.label);
        }
      }),
    [sortBy, tags]
  );

  if (tags.length === 0) {
    return (
      <div className="export-list-items-empty">
        <Typography variant="body-5" color="#37353B">
          {i18n('all.tags.empty.list')}
        </Typography>
      </div>
    );
  }

  return (
    <table className="export-list-items-table">
      <thead>
        <tr>
          <th onClick={handleSortToggle} style={{ cursor: 'pointer' }} className="row-header">
            <Typography variant="body-5" fontWeight="bold" color="#547ba2">
              {i18n('tag.label')}
            </Typography>

            <i className={`fa-solid fa-arrow-down ${sortBy === 'desc' ? 'rotate-180' : ''}`} />
          </th>
          <th>
            <Typography variant="body-5" fontWeight="bold" color="#547ba2">
              {i18n('tag.type')}
            </Typography>
          </th>
          <th>
            <Typography variant="body-5" fontWeight="bold" color="#547ba2">
              {i18n('tag.location.label')}
            </Typography>
          </th>
          <th>
            <Typography variant="body-5" fontWeight="bold" color="#547ba2">
              {i18n('tag.severity.label') || 'Severity'}
            </Typography>
          </th>
          <th></th>
        </tr>
      </thead>

      <tbody>
        {sortedSpecifics.map((tag) => {
          const tagActions: TagAction[] = [
            {
              type: 'details',
              onClick: () => onFindingDetailsClick?.(tag),
            },
          ];

          return (
            <tr key={tag.id}>
              <td>
                <Typography variant="body-5" fontWeight="semibold" color="#37353B">
                  {tag.label}
                  {tag.affectedCount && tag.affectedCount > 1 ? ` · ${tag.affectedCount} taggar` : ''}
                </Typography>
              </td>
              <td>
                <Typography variant="body-5" fontWeight="semibold" color="#6B7076">
                  {tag.type}
                </Typography>
              </td>
              <td>
                {tag.affectedPages && tag.affectedPages.length === 1 && onNavigateToPage ? (
                  <button type="button" className="export-list-items-table__page-link" onClick={() => onNavigateToPage(tag.affectedPages![0])}>
                    {tag.locationLabel}
                  </button>
                ) : (
                  <Typography variant="body-5" fontWeight="semibold" color="#6B7076">
                    {tag.locationLabel ?? '—'}
                  </Typography>
                )}
              </td>
              <td>
                <span className={`export-list-items-table__tag-status export-list-items-table__tag-status--${tag.status}`}>
                  {i18n(`tag.status.${tag.status}`)}
                </span>
              </td>
              <td>
                <TagItemShortcuts actions={tagActions} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
